/* Shared "Join the shortlist" modal for services that aren't live in
   production yet (Management Accounts, Business
   Valuation). Opens whenever a user clicks a locked service on the
   production build — the same feature stays fully clickable on the
   preview environment (see /app/frontend/src/lib/platform.js).

   Wraps the existing POST /api/waitlist endpoint (which supports
   accounts / financial_model / business_valuation / pitch_deck /
   general services). Success flips the modal into a confirmation
   state so the founder gets a clear "we'll notify you" beat. */
import React, { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, CheckCircle, EnvelopeSimple } from "@phosphor-icons/react";

const C = {
  teal: "#0A8B85",
  tealSoft: "#7BD3CE",
  orange: "#F59E0B",
  panel: "#0A2826",
};

const SERVICE_COPY = {
  accounts: {
    title: "Management Accounts",
    body: "Board-ready monthly management accounts on autopilot — coming soon. Join the shortlist and we'll notify you as soon as it opens.",
  },
  business_valuation: {
    title: "Business Valuation",
    body: "Multi-method valuation engine (DCF + comps + precedent) tuned to your industry — coming soon. Join the shortlist and we'll ping you when it launches.",
  },
  general: {
    title: "Clinq · early access",
    body: "Join the shortlist and we'll notify you when new features roll out.",
  },
};

export default function WaitlistModal({ open, service = "general", onClose, initialEmail = "" }) {
  const copy = SERVICE_COPY[service] || SERVICE_COPY.general;
  const [email, setEmail] = useState(initialEmail);
  const [company, setCompany] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [already, setAlready] = useState(false);

  useEffect(() => {
    if (open) {
      setEmail(initialEmail || "");
      setCompany("");
      setBusy(false);
      setDone(false);
      setAlready(false);
    }
  }, [open, initialEmail]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    const value = (email || "").trim();
    if (!value || !value.includes("@")) {
      toast.error("Enter a valid email so we can notify you.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/waitlist", {
        email: value,
        service,
        company: company.trim() || undefined,
        source: `production_gate:${service}`,
      });
      setAlready(!!data?.already_on_list);
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not add you to the list — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] grid place-items-center p-4"
      data-testid="waitlist-modal"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-md rounded-2xl border overflow-hidden"
        style={{ backgroundColor: C.panel, borderColor: "rgba(123, 211, 206, 0.22)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-5 border-b flex items-start justify-between gap-3"
          style={{ borderColor: "rgba(123, 211, 206, 0.12)" }}>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.22em]" style={{ color: C.orange }}>
              Coming soon · join the shortlist
            </div>
            <h2 className="mt-1 text-lg font-extrabold tracking-tight text-white"
              style={{ letterSpacing: "-0.02em" }}>
              {copy.title}
            </h2>
          </div>
          <button onClick={onClose} data-testid="waitlist-close"
            className="text-white/55 hover:text-white p-1 rounded shrink-0" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {done ? (
          <div className="p-6 space-y-3 text-sm text-white/80" data-testid="waitlist-success">
            <div className="inline-flex items-center gap-2 font-bold" style={{ color: C.tealSoft }}>
              <CheckCircle size={18} weight="fill" />
              {already ? "You're already on the list" : "You're on the shortlist"}
            </div>
            <p>
              We&apos;ll email <strong className="text-white">{email}</strong> the moment {copy.title.toLowerCase()} opens.
              In the meantime, our pitch deck builder is fully live — <a href="/pitch/new" className="underline" style={{ color: C.tealSoft }}>start yours</a>.
            </p>
            <div className="pt-2">
              <button onClick={onClose} data-testid="waitlist-done-btn"
                className="w-full py-2.5 rounded-md font-semibold text-white transition-colors"
                style={{ backgroundColor: C.teal }}>
                Got it
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 space-y-4">
            <p className="text-sm text-white/70 leading-relaxed">{copy.body}</p>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 font-semibold">Email</label>
              <input
                type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                autoFocus
                data-testid="waitlist-email-input"
                placeholder="founder@yourco.com"
                className="mt-1.5 w-full px-3 py-2.5 rounded-md text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#0A8B85]"
                style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }}
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 font-semibold">Company (optional)</label>
              <input
                type="text" value={company} onChange={(e) => setCompany(e.target.value)}
                data-testid="waitlist-company-input"
                placeholder="Your Co."
                className="mt-1.5 w-full px-3 py-2.5 rounded-md text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#0A8B85]"
                style={{ backgroundColor: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)" }}
              />
            </div>
            <button
              type="submit" disabled={busy}
              data-testid="waitlist-submit"
              className="w-full py-2.5 rounded-md font-semibold text-white transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
              style={{ backgroundColor: C.teal }}
            >
              <EnvelopeSimple size={15} weight="fill" />
              {busy ? "Adding you…" : "Notify me when it's ready"}
            </button>
            <div className="text-[11px] text-white/40 text-center">
              Pitch Deck Builder is live today — no waitlist needed.
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
